import React, { useState } from 'react'
import {
  Accordion,
  RawAccordion,
  Button,
  Checkbox,
  Dropdown,
  Pill,
} from '@scentregroup/ui'
import { OnSearchFunc } from '@scentregroup/shared/hub-components/searchable-faceted-filter'
import { IFacet, IFacetSelections } from '../../types/facets'
import { ICategory } from '../../types/marketplace-categories'
import { isFacetOptionSelected } from '../../lib/facets'
import trackMkpEvents, { labels } from '../../lib/market-place-track-event'
import SortOptionsDropdown, {
  DropdownOption,
  SortOption,
} from '../sort-options'
import { onSelectById } from './faceted-filters-utils'
import commonStyle from './index.module.scss'
import style from './small-faceted-filters.module.scss'
import { FilterType } from '.'
import FacetedFilter from './faceted-filter'
import ProductTypeFilter from './product-type-filter'
import { ICategoryList } from './category-list'

interface ISmallFacetedFiltersProps {
  category?: ICategory
  categoryListElement: React.FunctionComponentElement<ICategoryList> | undefined
  facets: IFacet[]
  facetSelections: IFacetSelections
  onSale: boolean
  hasOnSaleProducts?: boolean
  productTypeSelections: string[]
  sort: SortOption
  sortOptions?: DropdownOption[] | undefined
  onChangeFacetSelections(facetSelections: IFacetSelections): void
  onChangeProductTypeSelections(productTypeSelections: string[]): void
  onChangeSort(option: SortOption): void
  onClearAll(): void
  onFacetOptionSearch?: OnSearchFunc
  onSaleOnChange(value: boolean): void
}

type FilterExpandState = Map<string, boolean>

const CATEGORY_ACCORDION_ID = 'raw-accordion-category'
const PRODUCT_TYPES_ACCORDION_ID = 'raw-accordion-product-types'

const filterExpandedInitialState = (facets: IFacet[]): FilterExpandState => {
  const result = new Map()
  for (const facet of facets) {
    result.set(facet.id, false)
  }
  result.set(CATEGORY_ACCORDION_ID, false)
  result.set(PRODUCT_TYPES_ACCORDION_ID, false)
  return result
}

export const SmallFacetedFilters: React.FunctionComponent<
  React.PropsWithChildren<ISmallFacetedFiltersProps>
> = ({
  category,
  categoryListElement,
  facets,
  facetSelections,
  onSale,
  hasOnSaleProducts,
  productTypeSelections,
  sort,
  sortOptions,
  onChangeFacetSelections,
  onChangeProductTypeSelections,
  onChangeSort,
  onClearAll,
  onFacetOptionSearch,
  onSaleOnChange,
}) => {
  // Note: This state implementation assumes that the list of facets does not change overtime
  // Initially set based on the initial props
  const [expandedFilters, setExpandedFilters] = useState(
    filterExpandedInitialState(facets)
  )
  const countOfFacetsWithSelections = facetSelections.filter(
    f => f.selections.length > 0
  ).length

  const onClickFacetAccordion = (facetId: string): void => {
    const expandState = new Map<string, boolean>()
    expandedFilters.forEach((expanded, id) => {
      expandState.set(id, id === facetId ? !expanded : false)
    })
    setExpandedFilters(expandState)
  }

  return (
    <>
      <Accordion
        id={`small-filter`}
        type={'small'}
        classNames={{
          wrapper: style.accordionWrapper,
          header: style.accordionHeader,
          content: style.accordionContentWrapper,
        }}
        headerElement={
          <div className={style.header}>
            <span className={commonStyle.headerText}>Filter</span>
            <Pill className={style.accordionCount}>
              {countOfFacetsWithSelections.toString()}
            </Pill>
          </div>
        }
        onClick={() => {
          trackMkpEvents.expanded(labels.FILTERS_MOBILE)
        }}
      >
        <div className={style.sortDropdown}>
          {hasOnSaleProducts && (
            <div className={style.onSaleCheckboxWrapper}>
              <Checkbox
                checked={onSale}
                classNames={{
                  wrapper: style.onSaleCheckbox,
                  label: style.onSaleCheckboxLabel,
                  field: style.onSaleCheckboxField,
                }}
                label="Sale items"
                onChange={onSaleOnChange}
                size={'SMALL'}
                id="sale-items-small"
              />
            </div>
          )}
          <SortOptionsDropdown
            onChange={onChangeSort}
            option={sort}
            options={sortOptions}
            onClick={() => trackMkpEvents.expanded(labels.SORT_BY)}
          />
        </div>
        {categoryListElement && category && category.children.length > 0 ? (
          <RawAccordion
            key={CATEGORY_ACCORDION_ID}
            label="Category"
            id={CATEGORY_ACCORDION_ID}
            type="small"
            classNames={{
              wrapper: style.facetedFilterWrapper,
              content: commonStyle.facetedFilterContent,
            }}
            onClick={() => {
              onClickFacetAccordion(CATEGORY_ACCORDION_ID)
              trackMkpEvents.expanded(labels.createFiltersFacet('Category'))
            }}
            expanded={expandedFilters.get(CATEGORY_ACCORDION_ID) ?? false}
          >
            {categoryListElement}
          </RawAccordion>
        ) : (
          category?.productTypes &&
          category.productTypes.length > 0 && (
            <RawAccordion
              label={'Type'}
              id={PRODUCT_TYPES_ACCORDION_ID}
              type={'small'}
              classNames={{
                wrapper: style.facetedFilterWrapper,
                content: commonStyle.facetedFilterContent,
              }}
              onClick={() => {
                onClickFacetAccordion(PRODUCT_TYPES_ACCORDION_ID)
                trackMkpEvents.clicked(labels.createFiltersFacet('Type'))
              }}
              expanded={
                expandedFilters.get(PRODUCT_TYPES_ACCORDION_ID) ?? false
              }
            >
              <div className={style.categoryTypeFilter}>
                <ProductTypeFilter
                  productTypes={category.productTypes}
                  productTypeSelections={productTypeSelections}
                  onChangeProductTypeSelections={onChangeProductTypeSelections}
                />
              </div>
            </RawAccordion>
          )
        )}
        {facets.map((facet: IFacet) => {
          const { id, title, options, type } = facet
          return (
            <RawAccordion
              key={`${category?.path.map(pc => pc.code).join()}-${id}`}
              label={title}
              id={id}
              type={'small'}
              classNames={{
                wrapper: style.facetedFilterWrapper,
                content: commonStyle.facetedFilterContent,
              }}
              onClick={() => {
                onClickFacetAccordion(id)
                trackMkpEvents.clicked(labels.createFiltersFacet(title))
              }}
              expanded={expandedFilters.get(id) ?? false}
            >
              {type && type === FilterType.SINGLE_CHOICE ? (
                <Dropdown
                  id={id}
                  size="SMALL"
                  options={options.map(option => ({
                    value: option.id,
                    label: option.label,
                  }))}
                  value={
                    options.find(option =>
                      isFacetOptionSelected(facetSelections, id, option.id)
                    )?.id ?? ''
                  }
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const selection = event.target.value && [event.target.value]

                    onSelectById(
                      id,
                      selection || [],
                      facets,
                      facetSelections,
                      onChangeFacetSelections
                    )
                  }}
                />
              ) : (
                <FacetedFilter
                  facet={facet}
                  allFacets={facets}
                  facetSelections={facetSelections}
                  onChangeFacetSelections={onChangeFacetSelections}
                  onSearch={onFacetOptionSearch}
                />
              )}
            </RawAccordion>
          )
        })}
        <div className={style.actionClearWrapper}>
          <Button
            type="tertiary"
            className={commonStyle.actionClear}
            onClick={onClearAll}
          >
            Clear all
          </Button>
        </div>
      </Accordion>
    </>
  )
}
